// 寛容な CSV パーサ(RFC4180 風: 引用符/二重引用符エスケープ/引用内改行/CRLF 対応)と、
// CSV 書き出しの共通ヘルパ。SmartHR・カオナビ等の従業員一覧 CSV を読むために、
// 列はヘッダ名キーワードで自動検出する。

export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i]!;
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i += 1;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\n") {
      row.push(field);
      field = "";
      rows.push(row);
      row = [];
    } else if (c !== "\r") {
      field += c;
    }
  }
  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // 末尾の空行(空フィールド1個だけの行)は除外
  return rows.filter((r) => !(r.length === 1 && r[0] === ""));
}

// 数式インジェクション対策。監査 CSV は Excel で開かれる前提(BOM も付けている)で、
// SF/GW のユーザー名・プロファイル名・権限セット名はテナント側が自由に付けられるため、
// `=`/`+`/`-`/`@`/タブ/CR で始まる値をそのまま書き出すと監査担当者の Excel で数式として
// 評価される(外部通信によるデータ持ち出しの踏み台になる)。先頭に ' を付けて文字列に固定する。
// ただし純粋な数値(-1 等)は数式になり得ないので汚染しない(正当な負数の表示を壊さないため)。
const FORMULA_LEAD = /^[=+\-@\t\r]/;
const PLAIN_NUMBER = /^-?\d+(?:\.\d+)?$/;

function neutralizeFormula(v: string): string {
  return FORMULA_LEAD.test(v) && !PLAIN_NUMBER.test(v) ? `'${v}` : v;
}

// CSV 1 フィールドの出力用エスケープ。まず数式を無害化し、そのうえで引用(RFC4180)する。
// 引用符・カンマ・改行(LF/CR いずれも)を含む値だけを引用符で囲み、内部の " は "" に倍化する。
// 単独 CR を引用しないとレコード境界が壊れるため含める。
// 本関数は「書き出し専用」で、取り込み側(parseCsv)は通らない。全 CSV 出力経路
// (チェックシート / 検出 / ユーザー棚卸し / 権限マップ)がここを共有するため、対策は一箇所で効く。
export function escapeCsvField(v: string): string {
  const s = neutralizeFormula(v);
  return /[",\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

// 生成済み CSV 文字列をブラウザでダウンロードさせる(サーバ送信なし)。
// 先頭に UTF-8 BOM を付ける: 日本語 Excel は BOM 無し UTF-8 を Shift_JIS と誤認して
// ダブルクリック起動時に全列が文字化けするため(国内 B2B では CSV は Excel で開かれる前提)。
const UTF8_BOM = "﻿";

export function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([UTF8_BOM + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export interface ColumnIndices {
  readonly email: number;
  readonly name: number;
  readonly status: number; // 在籍状況(任意)
}

const EMAIL_KEYWORDS = ["mail", "メール", "電子メール"]; // "e-mail"/"email" は "mail" に部分一致
const NAME_KEYWORDS = ["氏名", "名前", "name", "社員名", "従業員名"];
const STATUS_KEYWORDS = ["在籍", "雇用状況", "status", "就業状況"];

function findColumn(header: readonly string[], keywords: readonly string[]): number {
  return header.findIndex((h) => {
    const lower = h.toLowerCase();
    return keywords.some((k) => lower.includes(k));
  });
}

// ヘッダ行から メール/氏名/在籍状況 列を自動検出する。見つからない列は -1。
export function detectColumns(header: readonly string[]): ColumnIndices {
  return {
    email: findColumn(header, EMAIL_KEYWORDS),
    name: findColumn(header, NAME_KEYWORDS),
    status: findColumn(header, STATUS_KEYWORDS),
  };
}
